import { useContext, useEffect, useCallback } from 'react';

import { CentrifugoContext } from '../context-providers/CentrifugoContext';
import type { NotificationCallback } from '../types';

interface UseCentrifugoNotificationsOptions {
  onDialogueUpdated?: NotificationCallback;
  onActivitiesUpdated?: NotificationCallback;
}

/**
 * Hook to subscribe to Centrifugo real-time notifications.
 *
 * Registers callbacks that are invoked when the server pushes
 * dialogue_updated or activities_updated events for the selected customer.
 * The callbacks receive the customer_id as a string parameter.
 *
 * @example
 * useCentrifugoNotifications({
 *   onDialogueUpdated: (customerId) => refetchDialogue(),
 *   onActivitiesUpdated: (customerId) => refetchActivities(),
 * });
 */
export const useCentrifugoNotifications = ({
  onDialogueUpdated,
  onActivitiesUpdated,
}: UseCentrifugoNotificationsOptions) => {
  const centrifugo = useContext(CentrifugoContext);

  useEffect(() => {
    if (!centrifugo) return;

    const cleanupFunctions: (() => void)[] = [];

    if (onDialogueUpdated) {
      cleanupFunctions.push(centrifugo.onDialogueUpdated(onDialogueUpdated));
    }

    if (onActivitiesUpdated) {
      cleanupFunctions.push(centrifugo.onActivitiesUpdated(onActivitiesUpdated));
    }

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [centrifugo, onDialogueUpdated, onActivitiesUpdated]);

  return { isConnected: centrifugo?.isConnected ?? false };
};

/**
 * Creates a memoized notification handler that only triggers refetch
 * when the notification is for the specified customer.
 *
 * @example
 * const handleNotification = useNotificationHandler(customerId, refetch);
 * useCentrifugoNotifications({ onDialogueUpdated: handleNotification });
 */
export const useNotificationHandler = (
  customerId: number | undefined,
  refetch: () => void,
): NotificationCallback => {
  return useCallback(
    (notificationCustomerId: string) => {
      if (customerId && String(customerId) === notificationCustomerId) {
        refetch();
      }
    },
    [customerId, refetch],
  );
};
